var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { resolveChoice } from "utility/choice-columns";
/*
 * Pumps are picked in two steps, family → model, like suction cups. The
 * family comes from Tacton (`VacuumPumpComponent.family`); a pump without one
 * (every pump until Tacton sends the field) is shown under every family, and a
 * list with no families at all is shown flat by the pickers.
 */
/** Distinct, non-empty families in the order the pumps were given. */
export var getPumpFamilies = function (pumps) {
    return pumps
        .map(function (pump) { return pump.family; })
        .filter(function (family, index, self) { return family && self.indexOf(family) === index; });
};
/** One representative image per family: the first pump in it with an image. */
export var getPumpFamilyImages = function (pumps, families) {
    return families.map(function (family) { var _a; return (_a = pumps.find(function (pump) { return pump.family === family && pump.image; })) === null || _a === void 0 ? void 0 : _a.image; });
};
/**
 * Whether a pump belongs in the list for the given families. With no family
 * picked the whole list shows, and pumps without a family always show.
 */
export var isPumpInFamilies = function (pump, families) {
    return !families.length || !pump.family || families.includes(pump.family);
};
/**
 * The pump's nozzle & size, e.g. "SX12": the models of a family are listed in
 * groups of these. Tacton's `ejector_name`, else the ejector model, which
 * carries nearly the same value, for pumps Tacton does not name yet.
 */
export var getPumpNozzleSize = function (pump) {
    return pump.ejectorName || pump.ejectorModel || "";
};
/**
 * Orders the pumps the way the model list shows them — by nozzle & size, then
 * by number of ejectors ("Nozzle, Size & Features") — keeping Tacton's
 * order otherwise. The nozzle & size groups come in the order Tacton first
 * lists each.
 */
export var sortPumpsByNozzleSize = function (pumps) {
    var groupOrder = new Map();
    pumps.forEach(function (pump) {
        var nozzleSize = getPumpNozzleSize(pump);
        if (!groupOrder.has(nozzleSize))
            groupOrder.set(nozzleSize, groupOrder.size);
    });
    var groupIndex = function (pump) { var _a; return (_a = groupOrder.get(getPumpNozzleSize(pump))) !== null && _a !== void 0 ? _a : 0; };
    /* Array.prototype.sort is stable, so ties keep Tacton's order. */
    return __spreadArray([], __read(pumps), false).sort(function (a, b) { return groupIndex(a) - groupIndex(b) || a.ejectorCount - b.ejectorCount; });
};
/** The feed pressure Tacton recommends, or undefined when it has none. */
export var getRecommendedFeedPressureMpa = function (pump) {
    var pressure = Number(pump.recFeedPressureMpa);
    return Number.isFinite(pressure) && pump.recFeedPressureMpa !== ""
        ? pressure
        : undefined;
};
/*
 * Within a family the sidebar picks a pump column by column — nozzle & size,
 * number of ejectors, then functions — each column narrowed by the choices to
 * its left. The last column lists pumps rather than values, since a few
 * families (piINLINE plus, piSAFE) hold pumps that agree on all three.
 */
/** Tacton's functions, e.g. "ES ABO"; empty for a plain pump. */
export var getPumpFunctions = function (pump) { var _a, _b; return (_b = (_a = pump.functions) === null || _a === void 0 ? void 0 : _a.trim()) !== null && _b !== void 0 ? _b : ""; };
/**
 * The pump's full name as the pump sections show it, e.g. "piCOMPACT®10X
 * Bi03-2 x1 ES": family, nozzle & size, number of ejectors and functions, with
 * `noFunctionsLabel` ("Std") for a plain pump. Tacton's label, which leaves
 * out the family, for a pump without a family or nozzle & size.
 */
export var getPumpFullName = function (pump, noFunctionsLabel) {
    var nozzleSize = getPumpNozzleSize(pump);
    if (!pump.family || !nozzleSize)
        return pump.label;
    return [
        pump.family,
        nozzleSize,
        pump.ejectorCount > 0 ? "x".concat(pump.ejectorCount) : "",
        getPumpFunctions(pump) || noFunctionsLabel,
    ]
        .filter(Boolean)
        .join(" ");
};
export var getPumpChoice = function (pump) { return ({
    nozzleSize: getPumpNozzleSize(pump),
    ejectorCount: pump.ejectorCount,
    functions: getPumpFunctions(pump),
}); };
/**
 * The pump of `familyPumps` that best fits `wanted` (`resolveChoice`, in the
 * list order of `sortPumpsByNozzleSize`). So picking a nozzle keeps the number
 * of ejectors and the functions where that nozzle comes with them, and with
 * nothing wanted it is the top option of each column, as the picker lists
 * them.
 */
export var resolvePumpChoice = function (familyPumps, wanted) {
    return resolveChoice(sortPumpsByNozzleSize(familyPumps), getPumpChoice, ["nozzleSize", "ejectorCount", "functions"], wanted);
};
/**
 * The picker's three columns for the pumps of one family, with `selectedPump`
 * (when it is one of them) picked in each: every nozzle & size of the family,
 * the ejector counts of the picked nozzle & size, and the pumps that come with
 * both — named by their functions, or by their full label where functions do
 * not tell them apart.
 */
export var getPumpChoiceColumns = function (familyPumps, selectedPump) {
    var sorted = sortPumpsByNozzleSize(familyPumps);
    var selected = selectedPump && sorted.some(function (pump) { return pump.value === selectedPump.value; })
        ? getPumpChoice(selectedPump)
        : undefined;
    var distinct = function (values) { return Array.from(new Set(values)); };
    var nozzleSizes = distinct(sorted.map(getPumpNozzleSize));
    var withNozzleSize = sorted.filter(function (pump) { return getPumpNozzleSize(pump) === (selected === null || selected === void 0 ? void 0 : selected.nozzleSize); });
    var ejectorCounts = distinct(withNozzleSize.map(function (pump) { return pump.ejectorCount; })).sort(function (a, b) { return a - b; });
    /* Tacton's order, which lists the plain pump before the ones with functions. */
    var variants = familyPumps.filter(function (pump) {
        return getPumpNozzleSize(pump) === (selected === null || selected === void 0 ? void 0 : selected.nozzleSize) &&
            pump.ejectorCount === (selected === null || selected === void 0 ? void 0 : selected.ejectorCount);
    });
    var functions = variants.map(getPumpFunctions);
    var isAmbiguous = function (value) {
        return functions.indexOf(value) !== functions.lastIndexOf(value);
    };
    return {
        nozzleSizes: nozzleSizes.map(function (value) { return ({ value: value, label: value }); }),
        ejectorCounts: ejectorCounts.map(function (value) { return ({
            value: value,
            label: "x".concat(value),
        }); }),
        /* Only offered where it is a choice: a family without functions has one
         * pump per nozzle & size, unless their functions do not tell them apart. */
        hasVariantColumn: familyPumps.some(function (pump) { return getPumpFunctions(pump) !== ""; }) ||
            variants.length > 1,
        variants: variants.map(function (pump, i) { return ({
            value: pump.value,
            pumpLabel: pump.label,
            /* Undefined for the plain pump, which the picker names itself. */
            label: isAmbiguous(functions[i]) ? pump.label : functions[i] || undefined,
        }); }),
        selected: selected && __assign(__assign({}, selected), { pump: selectedPump === null || selectedPump === void 0 ? void 0 : selectedPump.value }),
    };
};
